import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';
import { useMount } from 'react-use';

import { numbersApi } from 'apis/numbers';
import { ratingApi } from 'apis/rating';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { SelectSurveyModal } from '../../../survey/components/select/selectSurveyModal';

import { PhoneContactPointHeadRow } from './components/phoneSurveysTableHeadRow/phoneSurveysTableHeadRow';
import { PhoneSurveyTableRow } from './components/phoneSurveysTableRow/phoneSurveysTableRow';
import { PhoneSurveyLinkTableDataItem } from './phoneSurveysTable.types';

/**
 * Represents a component that displays a table with the phone numbers with
 * survey links.
 */
export const PhoneSurveysTable = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const flags = useFeatureFlag(['csat-for-phone-via-sms']);

  const [
    readRatingSettings,
    {
      data: readRatingSettingsLinksResponse,
      isSuccess: readRatingSettingsLinksIsSuccess,
    },
  ] = ratingApi.useLazyReadRatingSettingsLinksQuery();

  const [phoneSurveyLinks, setPhoneSurveyLiks] = useState<
    readonly PhoneSurveyLinkTableDataItem[]
  >([]);
  const {
    items: phoneNumbers,
    asyncStatus: phoneNumbersAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: numbersApi.endpoints.readNumbers,
    initialArg: { limit: 12 },
    itemsSelector: ({ data }) => data.numbers,
  });

  // Finishes initializing after the data has been fetched.
  useEffect(() => {
    if (
      phoneNumbersAsyncStatus === 'fulfilled' &&
      phoneNumbers.length &&
      readRatingSettingsLinksIsSuccess
    ) {
      setRatingSettingsLinks(
        readRatingSettingsLinksResponse?.data.rating_settings_links ?? [],
      );
      setIsLoading(false);
    }
  }, [
    phoneNumbers,
    phoneNumbersAsyncStatus,
    readRatingSettingsLinksResponse,
    readRatingSettingsLinksIsSuccess,
  ]);

  const [ratingSettingsLinks, setRatingSettingsLinks] = useState<
    Satisfaction.RatingSettingsLink[]
  >([]);

  useMount(() => {
    readRatingSettings({ contactPointKind: 'phone_number' });
  });

  // Creates the table data items whenever the constituent parts are ready. The
  // queues can come back with empty array, so we don't require that to be
  // present.
  useEffect(() => {
    if (phoneNumbers.length) {
      setPhoneSurveyLiks(
        phoneNumbers.reduce<PhoneSurveyLinkTableDataItem[]>(
          (phoneSurveysLinks, phoneNumber) => [
            ...phoneSurveysLinks,
            {
              phoneNumber: phoneNumber,
              link: ratingSettingsLinks
                .filter(
                  (link) => link.contact_point === phoneNumber.e164_format,
                )
                .shift(),
            },
          ],
          [],
        ),
      );
    }
  }, [phoneNumbers, ratingSettingsLinks, readRatingSettingsLinksResponse]);

  /**
   * Handles whenever an item is changed in order to update the survey links
   * table to reflect the changes.
   */
  const handleLinkTableDataItemChanged = useCallback(
    (updatedTableDataItem: PhoneSurveyLinkTableDataItem) => {
      setPhoneSurveyLiks((tableData) =>
        tableData.map((tableDataItem) =>
          tableDataItem.phoneNumber.e164_format ===
          updatedTableDataItem.phoneNumber.e164_format
            ? updatedTableDataItem
            : tableDataItem,
        ),
      );
    },
    [],
  );

  return (
    <>
      <Table loading={isLoading} loadingStrategy="disabled" stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Number</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Survey</Table.Th>
            <Table.Th>Type</Table.Th>
            {flags.csatForPhoneViaSms && (
              <Table.Th
                tooltip="For numbers connected to SMS surveys, the text message
                      will display your Sender Name/ID to the customer instead
                      of the phone number.
                      Not available in all countries."
              >
                Sender Name
              </Table.Th>
            )}
            <Table.Th width="0">Enabled</Table.Th>
            <Table.Th width="0"></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {isLoading ? (
            <PhoneContactPointHeadRow />
          ) : (
            phoneSurveyLinks.map((phoneSurveyLink) => (
              <PhoneSurveyTableRow
                key={phoneSurveyLink.phoneNumber.e164_format}
                phoneSurveyLink={phoneSurveyLink}
                onItemChanged={handleLinkTableDataItemChanged}
                isSenderNameVisible={flags.csatForPhoneViaSms}
              />
            ))
          )}
        </Table.Tbody>

        <Table.Footer>
          <SimplePagination
            nextButtonClick={nextPage}
            nextButtonDisabled={!hasNextPage}
            previousButtonClick={previousPage}
            previousButtonDisabled={!hasPreviousPage}
          />
        </Table.Footer>
      </Table>

      <SelectSurveyModal />
    </>
  );
};
